import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Sheet from "../ui/Sheet";
import { XIcon } from "../ui/icons";
import Markdown from "../ui/Markdown";

// Web port of DiscussionQuestionsView
// (LibraryFeature/View/Screen/DiscussionQuestionsView.swift).
//
// The app titles the sheet with the service's title and renders the questions
// through a MarkdownView, so this does the same — the field can contain
// markdown even though today's sample content is plain paragraphs.

export interface DiscussionQuestions {
  /** The service's title, shown as the sheet's title. */
  title: string;
  content: string;
}

export interface DiscussionQuestionsSheetProps {
  questions: DiscussionQuestions | null;
  onClose: () => void;
}

export default function DiscussionQuestionsSheet(props: DiscussionQuestionsSheetProps) {
  const { t } = useTranslation("discussionQuestions");

  // Keep rendering the last content while the sheet animates out, so the
  // panel does not empty as it slides away.
  const [last, setLast] = useState<DiscussionQuestions | null>(null);

  useEffect(() => {
    if (props.questions) setLast(props.questions);
  }, [props.questions]);

  const questions = props.questions ?? last;

  const content = questions?.content.trim() ?? "";

  return <Sheet
    open={props.questions !== null}
    onClose={props.onClose}
    label={props.questions?.title ?? t("label")}
    // The app presents this one at the large detent; there is usually enough
    // text to fill it.
    detent="large"
  >
    {questions && <div className="flex min-h-0 flex-1 flex-col">
      {/* Inline navigation title: stays put while the questions scroll. */}
      <header className="relative flex min-h-14 shrink-0 items-center border-b border-separator px-4">
        <button
          type="button"
          onClick={props.onClose}
          aria-label={t("close")}
          className="absolute left-4 flex size-9 items-center justify-center rounded-full bg-gray-200/80 text-gray-700 transition-opacity duration-200 hover:opacity-75"
        >
          <XIcon />
        </button>

        <h2 className="mx-auto max-w-full truncate px-12 text-center font-medium text-gray-900">
          {questions.title}
        </h2>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">
        {content
          ? <Markdown>{content}</Markdown>
          : <p className="py-8 text-center text-[15px] text-gray-500">
              {t("empty")}
            </p>}
      </div>
    </div>}
  </Sheet>
}
